import { ResizeEdge } from './edit';
import { clamp, dayBounds, durationOf, snap, type GeometryOptions, type IdSpan } from './span';

/** The batched outcome of a merged-group resize: spans to update and members to delete. */
export interface GroupResizeResult {
  readonly changes: readonly IdSpan[];
  readonly deleteIds: readonly string[];
}

const NO_CHANGE: GroupResizeResult = { changes: [], deleteIds: [] };

/**
 * The outermost member on `edge`: the member whose boundary coincides with the group boundary;
 * several coinciding members resolve to the longest one, then the smaller id (redesign §2.3).
 */
function outermostMember(members: readonly IdSpan[], edge: ResizeEdge, boundary: number): IdSpan | undefined {
  let best: IdSpan | undefined;
  for (const member of members) {
    const memberBoundary = edge === ResizeEdge.End ? member.endMin : member.startMin;
    if (memberBoundary !== boundary) continue;
    if (
      best === undefined ||
      durationOf(member) > durationOf(best) ||
      (durationOf(member) === durationOf(best) && member.id < best.id)
    ) {
      best = member;
    }
  }
  return best;
}

/**
 * Resize a merged display group by dragging its outer `edge` to `toMin` (redesign §2.3).
 * Dragging outward extends only the outermost member. Dragging inward to the new boundary p
 * deletes the members lying entirely beyond p and cuts the members p crosses down to p; the
 * others are untouched. The group never flips and keeps the floor — the minimum duration, or
 * the group's own shorter length (2026-10b §3.3) — at the anchor (the opposite outer
 * boundary); the personal day is the only other limit. `toExact` marks a neighbour detent hit
 * (2026-10b §3.6): the boundary then keeps the exact minute instead of snapping to the step.
 * The caller applies the whole result in one transaction, one undo step
 * (`updateBlockSpans(changes, deleteIds)`).
 */
export function resolveGroupResize(
  members: readonly IdSpan[],
  edge: ResizeEdge,
  toMin: number,
  options: GeometryOptions,
  toExact = false,
): GroupResizeResult {
  if (members.length === 0) return NO_CHANGE;
  const bounds = dayBounds(options.dayStartMin);
  const groupStart = Math.min(...members.map((member) => member.startMin));
  const groupEnd = Math.max(...members.map((member) => member.endMin));
  const floor = Math.min(Math.max(groupEnd - groupStart, 1), options.minDuration);
  const snapped = clamp(toExact ? toMin : snap(toMin, options.step), bounds.startMin, bounds.endMin);

  if (edge === ResizeEdge.End) {
    const boundary = Math.max(snapped, groupStart + floor);
    if (boundary === groupEnd) return NO_CHANGE;
    if (boundary > groupEnd) {
      const outer = outermostMember(members, edge, groupEnd);
      if (outer === undefined) return NO_CHANGE;
      return { changes: [{ id: outer.id, startMin: outer.startMin, endMin: boundary }], deleteIds: [] };
    }
    return {
      changes: members
        .filter((member) => member.startMin < boundary && member.endMin > boundary)
        .map((member) => ({ id: member.id, startMin: member.startMin, endMin: boundary })),
      deleteIds: members.filter((member) => member.startMin >= boundary).map((member) => member.id),
    };
  }

  const boundary = Math.min(snapped, groupEnd - floor);
  if (boundary === groupStart) return NO_CHANGE;
  if (boundary < groupStart) {
    const outer = outermostMember(members, edge, groupStart);
    if (outer === undefined) return NO_CHANGE;
    return { changes: [{ id: outer.id, startMin: boundary, endMin: outer.endMin }], deleteIds: [] };
  }
  return {
    changes: members
      .filter((member) => member.startMin < boundary && member.endMin > boundary)
      .map((member) => ({ id: member.id, startMin: boundary, endMin: member.endMin })),
    deleteIds: members.filter((member) => member.endMin <= boundary).map((member) => member.id),
  };
}

/**
 * Move every member of a merged group by the same offset (redesign §2.3): the offset snaps by
 * the group start — unless `snapStart` is false (a keyboard move by a whole hour, 2026-10b
 * §3.5, which keeps an off-grid start off the grid) — and the whole group stays inside the
 * personal day. Returns the shifted spans for `updateBlockSpans`; a group that does not move
 * returns an empty list.
 */
export function moveGroup(
  members: readonly IdSpan[],
  deltaMin: number,
  options: GeometryOptions,
  snapStart = true,
): readonly IdSpan[] {
  if (members.length === 0) return [];
  const bounds = dayBounds(options.dayStartMin);
  const groupStart = Math.min(...members.map((member) => member.startMin));
  const groupEnd = Math.max(...members.map((member) => member.endMin));
  const target = clamp(
    snapStart ? snap(groupStart + deltaMin, options.step) : groupStart + deltaMin,
    bounds.startMin,
    bounds.endMin - (groupEnd - groupStart),
  );
  const delta = target - groupStart;
  if (delta === 0) return [];
  return members.map((member) => ({
    id: member.id,
    startMin: member.startMin + delta,
    endMin: member.endMin + delta,
  }));
}
